<script>
import { GlLink, GlSprintf } from '@gitlab/ui';
import { s__ } from '~/locale';
import { getDayDifference } from '~/lib/utils/datetime/date_calculation_utility';
import { helpPagePath } from '~/helpers/help_page_helper';
import {
  AGENT_PLATFORM_SESSION_RETENTION_LENGTH,
  AGENT_PLATFORM_STATUS_FAILED,
} from '../../constants';
import AgentFlowTriggeredUser from './agent_flow_triggered_user.vue';
import AgentFlowSessionMessage from './agent_flow_session_message.vue';

export default {
  name: 'AgentFlowEmptyState',
  components: {
    GlLink,
    GlSprintf,
    AgentFlowTriggeredUser,
    AgentFlowSessionMessage,
  },
  props: {
    createdAt: {
      type: String,
      required: true,
    },
    hasLogs: {
      type: Boolean,
      required: true,
    },
    updatedAt: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    isWithinRetention() {
      const daysSinceUpdate = getDayDifference(new Date(this.updatedAt), new Date());
      return daysSinceUpdate <= AGENT_PLATFORM_SESSION_RETENTION_LENGTH;
    },
    isFailed() {
      return this.status === AGENT_PLATFORM_STATUS_FAILED;
    },
    emptyStateItems() {
      if (!this.isWithinRetention) {
        return null;
      }

      const items = [];
      const isCreating = !this.hasLogs && !this.status;

      // First item: Session creation status
      items.push({
        id: isCreating ? 'creating-session' : 'created-session',
        icon: isCreating ? 'status_running' : 'status_success',
        text: isCreating
          ? s__('DuoAgentPlatform|Creating session')
          : s__('DuoAgentPlatform|Created session'),
        content: isCreating ? s__('DuoAgentPlatform|Takes a few seconds...') : null,
        showUser: !isCreating,
        variant: 'subtle',
        timestamp: this.createdAt,
      });

      // Second item: Job status (non-failed only; failed is rendered separately)
      if (!this.hasLogs && this.status && !this.isFailed) {
        items.push({
          id: 'starting-job',
          icon: 'status_running',
          text: s__('DuoAgentPlatform|Starting job'),
          content: s__('DuoAgentPlatform|Takes a few minutes...'),
          variant: 'subtle',
          timestamp: this.createdAt,
        });
      }

      return items;
    },
    retentionMessage() {
      return s__(
        'DuoAgentPlatform|Activity deleted after 30 days of inactivity. %{linkStart}Learn more.%{linkEnd}',
      );
    },
    showRetentionMessage() {
      return !this.isWithinRetention && !this.hasLogs;
    },
  },
  sessionRetentionPath: helpPagePath('user/duo_agent_platform/sessions/_index', {
    anchor: '#session-retention',
  }),
};
</script>

<template>
  <div
    v-if="showRetentionMessage"
    class="gl-p-4 gl-text-center"
    data-testid="retention-message-container"
  >
    <gl-sprintf :message="retentionMessage">
      <template #link="{ content }">
        <gl-link :href="$options.sessionRetentionPath" target="_blank">{{ content }}</gl-link>
      </template>
    </gl-sprintf>
  </div>

  <ul v-else class="gl-list-none gl-p-0">
    <agent-flow-session-message
      v-for="item in emptyStateItems"
      :key="item.id"
      :icon="item.icon"
      :icon-variant="item.variant"
      :title="item.text"
      :timestamp="item.timestamp"
    >
      <span v-if="item.showUser" data-testid="triggered-user-container">
        {{ __('by') }}
        <agent-flow-triggered-user class="gl-link" :user="user" />
      </span>
      <span v-if="item.content">{{ item.content }}</span>
    </agent-flow-session-message>

    <agent-flow-session-message
      v-if="isFailed && !hasLogs"
      icon="status_failed"
      icon-variant="danger"
      :title="s__('DuoAgentPlatform|Session failed')"
      :timestamp="updatedAt"
      data-testid="agent-flow-failed-state"
    />
  </ul>
</template>
